import { openDB, IDBPDatabase } from "idb";

const DB_NAME = "smeet-offline";
const DB_VERSION = 4;

// Entries older than 7 days are considered stale
const STALE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface OfflineDB {
  threads: {
    key: string;
    value: { id: string; data: any; updatedAt: number };
  };
  messages: {
    key: string;
    value: { id: string; threadId: string; data: any; time: number };
    indexes: { "by-thread": string };
  };
  users: {
    key: string;
    value: { id: string; data: any; updatedAt: number };
  };
  // v3: cached group metadata keyed by the URL token (slug or numeric id) so a
  // group chat can render its header offline / instantly, with the resolved
  // thread stored alongside for cache-first message hydration.
  groups: {
    key: string;
    value: { id: string; data: any; thread: any; updatedAt: number };
  };
  // v4: cached Topics overview keyed by group id. Stores the topics array AND
  // the per-topic latest-thread index so the TopicsList can paint instantly
  // on re-open (Telegram-style "Topics" view), bypassing the 1+N network
  // fetch on the cache-hit path. `threadsByTopicId` keys: the topic id, or
  // "general" for the implicit non-deletable General topic (topicId === null).
  topics: {
    key: string;
    value: {
      id: string;
      topics: any[];
      threadsByTopicId: Record<string, any>;
      updatedAt: number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null;

function getDB(): Promise<IDBPDatabase<OfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<OfflineDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("threads")) {
          db.createObjectStore("threads", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("messages")) {
          const msgStore = db.createObjectStore("messages", { keyPath: "id" });
          msgStore.createIndex("by-thread", "threadId");
        }
        if (!db.objectStoreNames.contains("users")) {
          db.createObjectStore("users", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("groups")) {
          db.createObjectStore("groups", { keyPath: "id" });
        }
        // v4: topics overview cache (one entry per group id)
        if (!db.objectStoreNames.contains("topics")) {
          db.createObjectStore("topics", { keyPath: "id" });
        }
        // v2: remove orphaned outbox store from v1
        if (db.objectStoreNames.contains("outbox")) {
          db.deleteObjectStore("outbox");
        }
      },
    }).catch((err) => {
      // Reset so the next call retries instead of returning a rejected promise forever
      dbPromise = null;
      throw err;
    });
  }
  return dbPromise;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function safeTimestamp(value: any): number {
  if (typeof value === "number" && !isNaN(value)) return value;
  if (typeof value === "string") {
    const ms = new Date(value).getTime();
    if (!isNaN(ms)) return ms;
  }
  return Date.now();
}

// ── Threads ─────────────────────────────────────────────────────────────────

export async function cacheThreads(threads: any[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("threads", "readwrite");
  const now = Date.now();

  for (const thread of threads.slice(0, 100)) {
    await tx.store.put({ id: String(thread.id), data: thread, updatedAt: now });
  }

  await tx.done;
}

// Evict a thread from the offline cache (deleted / hidden / left). Without this
// a stale record — cached while hidden:false — is re-painted on the next
// hydration and the archived/deleted chat flashes in the main list until the
// fresh network list lands. Mirrors removeCachedMessage.
export async function removeCachedThread(id: string): Promise<void> {
  const db = await getDB();
  await db.delete("threads", String(id));
}

export async function getCachedThreads(): Promise<any[]> {
  const db = await getDB();
  const all = await db.getAll("threads");
  // Sort by THREAD ACTIVITY time (same key the fresh-fetch path uses), not by
  // CACHE INSERTION time. Sorting by cache insertion produced visible reorder
  // when the page reloaded with stale cache — the cached order was rendered
  // first, then ~500 ms later fresh data resorted by activity arrived and
  // visibly shuffled the list (active chat would jump to top). Aligning the
  // sort key here makes the cached order match the fresh order, so the swap
  // is invisible: stable React keys + identical order → no DOM reorder.
  return all
    // Defensive: drop any record cached while it was hidden/archived so a stale
    // put can't flash in the main list before the fresh (hidden:false) fetch.
    .filter((item) => (item.data || {}).hidden !== true)
    .sort((a, b) => {
      const aData = a.data || {};
      const bData = b.data || {};
      const timeA = aData.lastMessage?.time || aData.updatedTime || aData.createdTime || "";
      const timeB = bData.lastMessage?.time || bData.updatedTime || bData.createdTime || "";
      return String(timeB).localeCompare(String(timeA));
    })
    .map((item) => item.data);
}

// ── Messages ────────────────────────────────────────────────────────────────

const MAX_MESSAGES_PER_THREAD = 200;

export async function cacheMessages(threadId: string, messages: any[]): Promise<void> {
  const db = await getDB();
  const tid = String(threadId);
  const tx = db.transaction("messages", "readwrite");

  for (const msg of messages.slice(0, MAX_MESSAGES_PER_THREAD)) {
    await tx.store.put({
      id: String(msg.id),
      threadId: tid,
      data: msg,
      time: safeTimestamp(msg.time),
    });
  }

  // Enforce per-thread limit: keep only the newest MAX_MESSAGES_PER_THREAD
  const threadMsgs = await tx.store.index("by-thread").getAll(tid);
  if (threadMsgs.length > MAX_MESSAGES_PER_THREAD) {
    threadMsgs.sort((a, b) => b.time - a.time);
    const toRemove = threadMsgs.slice(MAX_MESSAGES_PER_THREAD);
    for (const old of toRemove) {
      await tx.store.delete(old.id);
    }
  }

  await tx.done;
}

export async function getCachedMessages(threadId: string): Promise<any[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex("messages", "by-thread", String(threadId));
  return all.sort((a, b) => a.time - b.time).map((item) => item.data);
}

// Purge a deleted message from the offline cache. Without this the cached
// copy is re-hydrated on reload and the additive mergeMessages resurrects
// the message on screen even though the server deleted it.
export async function removeCachedMessage(messageId: string): Promise<void> {
  const db = await getDB();
  await db.delete("messages", String(messageId));
}

// ── Users ───────────────────────────────────────────────────────────────────

export async function cacheUser(user: any): Promise<void> {
  if (!user?.id) return;
  const db = await getDB();
  await db.put("users", { id: String(user.id), data: user, updatedAt: Date.now() });
}

export async function getCachedUser(userId: string): Promise<any | null> {
  const db = await getDB();
  const item = await db.get("users", userId);
  return item?.data ?? null;
}

// ── Groups ──────────────────────────────────────────────────────────────────

/** Cache a group (keyed by its URL token: slug or numeric id) with its thread. */
export async function cacheGroup(token: string, group: any, thread: any): Promise<void> {
  if (!token || !group) return;
  const db = await getDB();
  await db.put("groups", { id: String(token), data: group, thread: thread ?? null, updatedAt: Date.now() });
}

export async function getCachedGroup(token: string): Promise<{ group: any; thread: any } | null> {
  const db = await getDB();
  const item = await db.get("groups", String(token));
  return item ? { group: item.data, thread: item.thread } : null;
}

// ── Topics overview ─────────────────────────────────────────────────────────

/**
 * Cache the topics overview (general + custom topics) plus each topic's
 * latest backing thread. The TopicsList component hydrates instantly from
 * this on re-open instead of paying the 1+N network roundtrip every time
 * the user opens a topics-enabled group's overview.
 *
 * `threadsByTopicId` keys: the topic id, or "general" for the implicit
 * non-deletable General topic (`thread.topicId === null`).
 */
export async function cacheTopics(
  groupId: string,
  topics: any[],
  threadsByTopicId: Record<string, any>
): Promise<void> {
  if (!groupId) return;
  const db = await getDB();
  await db.put("topics", {
    id: String(groupId),
    topics,
    threadsByTopicId,
    updatedAt: Date.now(),
  });
}

export async function getCachedTopics(
  groupId: string
): Promise<{ topics: any[]; threadsByTopicId: Record<string, any> } | null> {
  if (!groupId) return null;
  const db = await getDB();
  const item = await db.get("topics", String(groupId));
  if (!item) return null;
  return { topics: item.topics, threadsByTopicId: item.threadsByTopicId };
}

// ── Thread lookup by opponent userId / URL token ────────────────────────────

const THREAD_MAP_KEY = "smeet_thread_map";
const SLUG_THREAD_MAP_KEY = "smeet_slug_thread_map";

function readMap(key: string): Record<string, string> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeMapEntry(key: string, mapKey: string, value: string): void {
  try {
    const map = readMap(key);
    map[mapKey] = String(value);
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function cacheThreadIdForUser(userId: string, threadId: string): void {
  writeMapEntry(THREAD_MAP_KEY, userId, threadId);
}

export function getCachedThreadIdForUser(userId: string): string | null {
  return readMap(THREAD_MAP_KEY)[userId] ?? null;
}

/**
 * Synchronous URL-token → threadId shortcut so opening a previously-visited
 * chat (by the exact slug/id in the URL) resolves a threadId with zero IndexedDB
 * reads, enabling an instant cache-first paint.
 */
export function cacheThreadIdForSlug(token: string, threadId: string): void {
  writeMapEntry(SLUG_THREAD_MAP_KEY, token, threadId);
}

export function getCachedThreadIdForSlug(token: string): string | null {
  return readMap(SLUG_THREAD_MAP_KEY)[token] ?? null;
}

export function clearThreadMap(): void {
  try {
    localStorage.removeItem(THREAD_MAP_KEY);
    localStorage.removeItem(SLUG_THREAD_MAP_KEY);
  } catch {
    /* ignore */
  }
}

// ── Cache management ────────────────────────────────────────────────────────

export interface CacheStats {
  threads: number;
  messages: number;
  users: number;
  totalEntries: number;
  estimatedSizeBytes: number;
  estimatedSizeMB: string;
}

export async function getCacheStats(): Promise<CacheStats> {
  const db = await getDB();
  const [threadCount, messageCount, userCount] = await Promise.all([
    db.count("threads"),
    db.count("messages"),
    db.count("users"),
  ]);
  const totalEntries = threadCount + messageCount + userCount;

  // Estimate size by sampling up to 5 entries per store via cursor (avoids loading all records)
  let estimatedSizeBytes = 0;
  try {
    const sampleStore = async (storeName: "threads" | "messages" | "users", count: number) => {
      if (count === 0) return 0;
      const tx = db.transaction(storeName, "readonly");
      let cursor = await tx.store.openCursor();
      let sampled = 0;
      let totalBytes = 0;
      while (cursor && sampled < 5) {
        totalBytes += JSON.stringify(cursor.value).length * 2; // UTF-16 estimate
        sampled++;
        cursor = await cursor.continue();
      }
      if (sampled === 0) return 0;
      return Math.round((totalBytes / sampled) * count);
    };
    const [tSize, mSize, uSize] = await Promise.all([
      sampleStore("threads", threadCount),
      sampleStore("messages", messageCount),
      sampleStore("users", userCount),
    ]);
    estimatedSizeBytes = tSize + mSize + uSize;
  } catch {
    estimatedSizeBytes = totalEntries * 2048;
  }

  return {
    threads: threadCount,
    messages: messageCount,
    users: userCount,
    totalEntries,
    estimatedSizeBytes,
    estimatedSizeMB: (estimatedSizeBytes / (1024 * 1024)).toFixed(2),
  };
}

export async function clearAllCache(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(["threads", "messages", "users", "groups"], "readwrite");
  await Promise.all([
    tx.objectStore("threads").clear(),
    tx.objectStore("messages").clear(),
    tx.objectStore("users").clear(),
    tx.objectStore("groups").clear(),
    tx.done,
  ]);
  clearThreadMap();
}

/** Remove entries older than STALE_TTL_MS. Call periodically (e.g. on app start). */
export async function pruneStaleEntries(): Promise<void> {
  const db = await getDB();
  const cutoff = Date.now() - STALE_TTL_MS;

  const txThreads = db.transaction("threads", "readwrite");
  let cursor = await txThreads.store.openCursor();
  while (cursor) {
    if (cursor.value.updatedAt < cutoff) await cursor.delete();
    cursor = await cursor.continue();
  }
  await txThreads.done;

  const txUsers = db.transaction("users", "readwrite");
  let uCursor = await txUsers.store.openCursor();
  while (uCursor) {
    if (uCursor.value.updatedAt < cutoff) await uCursor.delete();
    uCursor = await uCursor.continue();
  }
  await txUsers.done;

  const txMessages = db.transaction("messages", "readwrite");
  let mCursor = await txMessages.store.openCursor();
  while (mCursor) {
    if (mCursor.value.time < cutoff) await mCursor.delete();
    mCursor = await mCursor.continue();
  }
  await txMessages.done;

  const txGroups = db.transaction("groups", "readwrite");
  let gCursor = await txGroups.store.openCursor();
  while (gCursor) {
    if (gCursor.value.updatedAt < cutoff) await gCursor.delete();
    gCursor = await gCursor.continue();
  }
  await txGroups.done;
}
